import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { cn, Badge, PremiumPageHeader } from '@repo/ui';
import {
  ClipboardCheck,
  TrendingUp,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  Zap,
} from 'lucide-react';

const STATUS_CONFIG: Record<
  string,
  { label: string; icon: any; color: string; bg: string; border: string }
> = {
  present: {
    label: 'present',
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
  },
  absent: {
    label: 'absent',
    icon: XCircle,
    color: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
  },
  excused: {
    label: 'excused',
    icon: Calendar,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
  },
};

const AttendancePage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['student-attendance-summary'],
    queryFn: () => api.get('/student/attendance/summary').then((res) => res.data?.data),
  });

  const { data: records, isLoading: recordsLoading } = useQuery({
    queryKey: ['student-attendance'],
    queryFn: () => api.get('/student/attendance').then((res) => res.data?.data),
  });

  const isLoading = summaryLoading || recordsLoading;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-4 border-primary/20 animate-spin border-t-primary" />
          <ClipboardCheck className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
        </div>
        <p className="text-muted-foreground font-medium animate-pulse">{t('common.loading')}</p>
      </div>
    );
  }

  // Ensure we check all possible rate fields from both summary and dashboard-style responses
  const rate = summary?.attendanceRate ?? summary?.rate ?? 0;
  const circumference = 2 * Math.PI * 45;
  const dashOffset = circumference - (rate / 100) * circumference;

  // 3 colors threshold: Red < 60, Yellow < 85, Green >= 85
  const rateColor = rate < 60 ? 'text-rose-500' : rate < 85 ? 'text-amber-500' : 'text-emerald-500';

  return (
    <div className="space-y-8 pb-12">
      {/* ── Standardized Header ── */}
      <PremiumPageHeader
        title={t('student.attendance.title')}
        subtitle={t('student.attendance.subtitle', { defaultValue: 'Attendance Dashboard' })}
        icon={ClipboardCheck}
        iconClassName="text-indigo-400"
        iconContainerClassName="bg-indigo-500/10 border-indigo-500/20"
      >
        <div className="flex items-center gap-8 px-8 py-5 rounded-[2.5rem] bg-primary/10 border border-primary/20 backdrop-blur-xl shadow-2xl shadow-primary/5">
          <div className="relative h-16 w-16 shrink-0">
            <svg className="h-16 w-16 -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="rgba(var(--primary-rgb), 0.05)"
                strokeWidth="10"
                fill="none"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                strokeWidth="10"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                className={cn('transition-all duration-1000 ease-out', rateColor)}
              />
            </svg>
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center text-sm font-black tracking-tighter',
                rateColor
              )}
            >
              {rate}%
            </div>
          </div>
          <div className="h-14 w-px bg-primary/20" />
          <div>
            <p className="text-[11px] font-black tracking-[0.2em] text-primary/60 mb-1.5 uppercase">
              {t('student.attendance.totalRate')}
            </p>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-black text-white italic tracking-tighter">
                {summary?.attended ?? summary?.present ?? 0}
              </p>
              <span className="text-lg font-bold text-white/40">/ {summary?.total ?? 0}</span>
            </div>
          </div>
        </div>
      </PremiumPageHeader>

      {/* ── Dashboard Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            label: 'present',
            value: summary?.present ?? 0,
            icon: CheckCircle2,
            color: 'text-emerald-500',
            bg: 'bg-emerald-500/10',
          },
          {
            label: 'absent',
            value: summary?.absent ?? 0,
            icon: XCircle,
            color: 'text-rose-500',
            bg: 'bg-rose-500/10',
          },
          {
            label: 'excused',
            value: summary?.excused ?? 0,
            icon: Calendar,
            color: 'text-blue-500',
            bg: 'bg-blue-500/10',
          },
        ].map((item, idx) => (
          <div
            key={idx}
            className="group relative overflow-hidden rounded-[2rem] border border-border bg-card p-6 transition-all hover:shadow-xl duration-500 hover:-translate-y-1 hover:border-indigo-500/20 shadow-sm"
          >
            {/* Soft Background Decorator */}
            <div
              className={cn(
                'absolute -right-4 -top-4 h-24 w-24 rounded-full blur-3xl opacity-20 transition-opacity duration-500 group-hover:opacity-40',
                item.bg
              )}
            />

            <div className="relative z-10 flex items-center justify-between">
              <div className="space-y-1.5">
                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest opacity-60">
                  {t(`student.attendance.${item.label}`)}
                </p>
                <p className="text-4xl font-black transition-transform group-hover:scale-105 duration-500 tracking-tighter">
                  {item.value}
                </p>
              </div>
              <div
                className={`p-4 rounded-2xl ${item.bg} ${item.color} transition-all duration-500 group-hover:rotate-12 group-hover:shadow-lg shadow-inner`}
              >
                <item.icon className="h-7 w-7" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Session Records ── */}
      <div className="space-y-5">
        <div className="flex items-center justify-between px-2">
          <h2 className="text-lg font-black tracking-tight flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-indigo-500" />
            {t('student.attendance.recentRecords')}
          </h2>
          <span className="text-[10px] font-black  text-muted-foreground/40">
            {records?.length || 0} {t('student.attendance.sessions')}
          </span>
        </div>

        {records?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-muted/20 rounded-[2rem] border-2 border-dashed border-muted/50 text-center space-y-3">
            <Zap className="h-10 w-10 text-muted-foreground/20" />
            <p className="text-muted-foreground font-black  text-xs opacity-50">
              {t('student.attendance.noRecords')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {records.map((record: any) => {
              const statusCfg = STATUS_CONFIG[record.status] || STATUS_CONFIG.present;
              const StatusIcon = statusCfg.icon;
              const date = new Date(record.attendanceDate || record.date || record.createdAt);

              const sessionGroups = record.session?.groups || [];
              const groupName =
                sessionGroups[0]?.code || sessionGroups[0]?.name || t('common.generalSession');
              const isWorkshopException =
                record.sessionFormula === 'workshop-exception' ||
                record.session?.sessionFormula === 'workshop-exception';

              return (
                <div
                  key={record._id}
                  className="group relative overflow-hidden rounded-2xl border border-border bg-card p-4 transition-all duration-300 hover:shadow-md hover:border-indigo-500/20"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-105 shadow-inner ${statusCfg.bg} ${statusCfg.color}`}
                      >
                        <StatusIcon className="h-6 w-6" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Badge
                            variant="outline"
                            className={`text-[9px] font-black  px-2 py-0 border ${statusCfg.border} ${statusCfg.bg} ${statusCfg.color}`}
                          >
                            {t(`student.attendance.${statusCfg.label}`)}
                          </Badge>
                          {isWorkshopException && (
                            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" />
                          )}
                        </div>
                        <h3 className="text-sm font-black tracking-tight truncate group-hover:text-indigo-600 transition-colors">
                          {isWorkshopException
                            ? t('student.dashboard.workshopException')
                            : groupName}
                        </h3>
                        <div className="flex items-center gap-4 text-[11px] font-bold text-muted-foreground/60">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-indigo-400" />
                            {date.toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-GB', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                          <span
                            className={`${isRtl ? 'pr-3 border-r' : 'pl-3 border-l'} border-border flex items-center gap-1.5`}
                          >
                            <Clock className="w-3.5 h-3.5 text-indigo-400" />
                            {date.toLocaleTimeString(i18n.language === 'ar' ? 'ar-EG' : 'en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="hidden md:flex flex-col items-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-[9px] font-black text-emerald-600 ">
                        {t('common.verified')}
                      </span>
                      <span className="text-[9px] text-muted-foreground/40 font-bold">
                        {record.sessionFormula || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendancePage;
